import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  FilterList,
  Search,
  Close,
  Check,
  Error,
  AutoAwesome,
  Person,
  Business,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { Donor, CreateDonorRequest, Filters } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { 
  PageLayout, 
  ScrollableContent, 
  PageHeader, 
  StandardPagination,
  FilterChip 
} from '../components/Layout/index';
import { 
  Button, 
  Chip, 
  DataTable,
  useStatusColors,
  useActionColors,
  ActionButtons
} from '../design-system';
import GenericFilterModal, { FilterField } from '../components/GenericFilterModal';
import ViewModal, { ViewField } from '../components/ViewModal';
import { useFilterState } from '../hooks/useFilterState';
import type { Column } from '../design-system/components/organisms/DataTable/DataTable';


const Donors: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Stati locali per i filtri (senza debounce)
  const [localFilters, setLocalFilters] = useState({
    search: '',
    city: '',
    country: '',
    phone: '',
    gdprConsent: null as boolean | null,
    marketingConsent: null as boolean | null,
  });
  
  // Stati per la query (con debounce) - inizializzati vuoti per non filtrare all'avvio
  const [queryFilters, setQueryFilters] = useState({
    search: '',
    city: '',
    country: '',
    phone: '',
    gdprConsent: null as boolean | null,
    marketingConsent: null as boolean | null,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [editingDonor, setEditingDonor] = useState<Donor | null>(null);
  
  // Stati per validazione fiscale
  const [fiscalCodeValidation, setFiscalCodeValidation] = useState<{ isValid: boolean; error?: string } | null>(null);
  const [vatNumberValidation, setVatNumberValidation] = useState<{ isValid: boolean; error?: string } | null>(null);
  const [sdiCodeValidation, setSdiCodeValidation] = useState<{ isValid: boolean; error?: string } | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [fiscalFieldType, setFiscalFieldType] = useState<'fiscalCode' | 'vatNumber'>('fiscalCode');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingDonor, setViewingDonor] = useState<Donor | null>(null);
  const [formData, setFormData] = useState<CreateDonorRequest>({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'IT',
    gdprConsent: false,
    marketingConsent: false,
    fiscalCode: '',
    vatNumber: '',
    sdiCode: '',
    pec: '',
  });

  const { hasAnyRole } = useAuth();
  const queryClient = useQueryClient();
  const statusColors = useStatusColors();
  const actionColors = useActionColors();

  // Hook per gestire lo stato dei filtri
  const { hasActiveFilters, activeFiltersCount } = useFilterState(queryFilters);

  // I filtri vengono applicati solo quando l'utente clicca "Applica Filtri"
  // Non c'è bisogno di useEffect per applicarli automaticamente

  // Configurazione campi per la modale filtri
  const filterFields: FilterField[] = [
    {
      key: 'search',
      label: 'Cerca (Nome, Cognome, Email, Azienda, CF, P.IVA, SDI, PEC)',
      type: 'text',
      icon: <Search />,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'city',
      label: 'Città',
      type: 'text',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'country',
      label: 'Paese',
      type: 'text',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'phone',
      label: 'Telefono',
      type: 'text',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'gdprConsent',
      label: 'Consenso GDPR',
      type: 'select',
      options: [
        { value: 'true', label: 'Consentito' },
        { value: 'false', label: 'Non consentito' },
      ],
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'marketingConsent',
      label: 'Consenso Marketing',
      type: 'select',
      options: [
        { value: 'true', label: 'Consentito' },
        { value: 'false', label: 'Non consentito' },
      ],
      size: { xs: 12, sm: 6 },
    },
  ];

  // Valori correnti dei filtri
  const filterValues = {
    search: localFilters.search,
    city: localFilters.city,
    country: localFilters.country,
    phone: localFilters.phone,
    gdprConsent: localFilters.gdprConsent === null ? '' : localFilters.gdprConsent.toString(),
    marketingConsent: localFilters.marketingConsent === null ? '' : localFilters.marketingConsent.toString(),
  };

  // Funzione per applicare i filtri
  const applyFilters = useCallback(() => {
    setQueryFilters(localFilters);
  }, [localFilters]);

  // Funzione per pulire i filtri
  const clearFilters = useCallback(() => {
    const emptyFilters = {
      search: '',
      city: '',
      country: '',
      phone: '',
      gdprConsent: null as boolean | null,
      marketingConsent: null as boolean | null,
    };
    setLocalFilters(emptyFilters);
    setQueryFilters(emptyFilters);
    setPage(0);
  }, []);

  // Funzioni per aggiornare i filtri locali (senza re-render)
  const updateFilter = useCallback((key: string, value: any) => {
    if (key === 'gdprConsent' || key === 'marketingConsent') {
      const boolValue = value === '' ? null : value === 'true';
      setLocalFilters(prev => ({ ...prev, [key]: boolValue }));
    } else {
      setLocalFilters(prev => ({ ...prev, [key]: value }));
    }
  }, []);

  // Genera i campi per la modale di visualizzazione donatore
  const getDonorViewFields = (donor: Donor): ViewField[] => [
    {
      key: 'firstName',
      label: 'Nome',
      value: donor.firstName,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'lastName',
      label: 'Cognome',
      value: donor.lastName,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'email',
      label: 'Email',
      value: donor.email,
      type: 'email',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'company',
      label: 'Azienda',
      value: donor.company,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'phone',
      label: 'Telefono',
      value: donor.phone,
      type: 'phone',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'city',
      label: 'Città',
      value: donor.city,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'country',
      label: 'Paese',
      value: donor.country,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'postalCode',
      label: 'CAP',
      value: donor.postalCode,
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'address',
      label: 'Indirizzo',
      value: donor.address,
      type: 'address',
      size: { xs: 12 },
    },
    {
      key: 'fiscalCode',
      label: 'Codice Fiscale',
      value: donor.fiscalCode,
      size: { xs: 12, sm: 4 },
    },
    {
      key: 'vatNumber',
      label: 'Partita IVA',
      value: donor.vatNumber,
      size: { xs: 12, sm: 4 },
    },
    {
      key: 'sdiCode',
      label: 'Codice Univoco SDI',
      value: donor.sdiCode,
      size: { xs: 12, sm: 4 },
    },
    {
      key: 'pec',
      label: 'PEC',
      value: donor.pec,
      type: 'email',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'gdprConsent',
      label: 'Consenso GDPR',
      value: donor.gdprConsent ? 'Sì' : 'No',
      type: 'chip',
      chipColor: donor.gdprConsent ? 'success' : 'error',
      size: { xs: 12, sm: 6 },
      customRender: (value) => (
        <Box>
          <Chip
            label={String(value || 'N/A')}
            color={donor.gdprConsent ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
          {donor.gdprConsentDate && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Data: {new Date(donor.gdprConsentDate).toLocaleDateString('it-IT')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'marketingConsent',
      label: 'Consenso Marketing',
      value: donor.marketingConsent ? 'Sì' : 'No',
      type: 'chip',
      chipColor: donor.marketingConsent ? 'success' : 'default',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'createdAt',
      label: 'Data Creazione',
      value: donor.createdAt,
      type: 'date',
      size: { xs: 12, sm: 6 },
    },
    {
      key: 'updatedAt',
      label: 'Ultima Modifica',
      value: donor.updatedAt,
      type: 'date',
      size: { xs: 12, sm: 6 },
    },
  ];

  const filters: Filters = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage,
    search: queryFilters.search || undefined,
    city: queryFilters.city || undefined,
    country: queryFilters.country || undefined,
    phone: queryFilters.phone || undefined,
    gdprConsent: queryFilters.gdprConsent !== null ? queryFilters.gdprConsent : undefined,
    marketingConsent: queryFilters.marketingConsent !== null ? queryFilters.marketingConsent : undefined,
  }), [page, rowsPerPage, queryFilters]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['donors', filters],
    queryFn: () => apiService.getDonors(filters),
  });


  const createMutation = useMutation({
    mutationFn: (donor: CreateDonorRequest) => apiService.createDonor(donor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donors'] });
      setOpenDialog(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDonorRequest> }) =>
      apiService.updateDonor(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donors'] });
      setOpenDialog(false);
      setEditingDonor(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteDonor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donors'] });
    },
  });

  const resetForm = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'IT',
      gdprConsent: false,
      marketingConsent: false,
      fiscalCode: '',
      vatNumber: '',
      sdiCode: '',
      pec: '',
    });
    setFiscalFieldType('fiscalCode');
    setFiscalCodeValidation(null);
    setVatNumberValidation(null);
    setSdiCodeValidation(null);
  };

  const handleOpenDialog = (donor?: Donor) => {
    if (donor) {
      setEditingDonor(donor);
      
      // Determina quale campo fiscale è presente
      const hasFiscalCode = donor.fiscalCode && donor.fiscalCode.trim() !== '';
      const hasVatNumber = donor.vatNumber && donor.vatNumber.trim() !== '';
      
      if (hasFiscalCode) {
        setFiscalFieldType('fiscalCode');
      } else if (hasVatNumber) {
        setFiscalFieldType('vatNumber');
      } else {
        setFiscalFieldType('fiscalCode'); // Default
      }
      
      setFormData({
        email: donor.email,
        firstName: donor.firstName,
        lastName: donor.lastName,
        company: donor.company || '',
        phone: donor.phone || '',
        address: donor.address || '',
        city: donor.city || '',
        postalCode: donor.postalCode || '',
        country: donor.country,
        gdprConsent: donor.gdprConsent,
        marketingConsent: donor.marketingConsent,
        fiscalCode: donor.fiscalCode || '',
        vatNumber: donor.vatNumber || '',
        sdiCode: donor.sdiCode || '',
        pec: donor.pec || '',
      });
    } else {
      setEditingDonor(null);
      setFiscalFieldType('fiscalCode'); // Default per nuovo donatore
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDonor(null);
    resetForm();
  };

  const handleViewDonor = (donor: Donor) => {
    setViewingDonor(donor);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingDonor(null);
  };

  // Funzioni di validazione fiscale
  const validateFiscalCode = async (fiscalCode: string) => {
    if (!fiscalCode || fiscalCode.length < 16) {
      setFiscalCodeValidation(null);
      return;
    }

    setIsValidating(true);
    try {
      const result = await apiService.validateFiscalCode(fiscalCode);
      setFiscalCodeValidation(result);
    } catch (error) {
      setFiscalCodeValidation({ isValid: false, error: 'Errore nella validazione' });
    } finally {
      setIsValidating(false);
    }
  };

  const validateVatNumber = async (vatNumber: string) => {
    if (!vatNumber || vatNumber.length < 11) {
      setVatNumberValidation(null);
      return;
    }

    setIsValidating(true);
    try {
      const result = await apiService.validateVatNumber(vatNumber);
      setVatNumberValidation(result);
    } catch (error) {
      setVatNumberValidation({ isValid: false, error: 'Errore nella validazione' });
    } finally {
      setIsValidating(false);
    }
  };

  const validateSdiCode = async (sdiCode: string) => {
    if (!sdiCode || sdiCode.length < 6) {
      setSdiCodeValidation(null);
      return;
    }

    setIsValidating(true);
    try {
      const result = await apiService.validateSdiCode(sdiCode);
      setSdiCodeValidation(result);
    } catch (error) {
      setSdiCodeValidation({ isValid: false, error: 'Errore nella validazione' });
    } finally {
      setIsValidating(false);
    }
  };

  // Funzione per cambiare il tipo di campo fiscale
  const handleFiscalFieldTypeChange = (type: 'fiscalCode' | 'vatNumber') => {
    setFiscalFieldType(type);
    
    // Reset dei campi fiscali quando si cambia tipo
    setFormData(prev => ({
      ...prev,
      fiscalCode: type === 'fiscalCode' ? prev.fiscalCode : '',
      vatNumber: type === 'vatNumber' ? prev.vatNumber : '',
    }));
    
    // Reset delle validazioni
    setFiscalCodeValidation(null);
    setVatNumberValidation(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validazione consenso GDPR obbligatorio
    if (!formData.gdprConsent) {
      alert('Il consenso al trattamento dei dati personali (GDPR) è obbligatorio per creare o modificare un donatore.');
      return;
    }
    
    // Validazione client-side: il campo fiscale selezionato deve essere presente
    const selectedFiscalField = fiscalFieldType === 'fiscalCode' ? formData.fiscalCode : formData.vatNumber;
    
    if (!selectedFiscalField || selectedFiscalField.trim() === '') {
      const fieldName = fiscalFieldType === 'fiscalCode' ? 'Codice Fiscale' : 'Partita IVA';
      alert(`È obbligatorio inserire il ${fieldName}`);
      return;
    }
    
    // Pulisci i dati prima di inviarli: rimuovi stringhe vuote e converte in undefined
    const cleanFormData = {
      ...formData,
      company: formData.company?.trim() || undefined,
      phone: formData.phone?.trim() || undefined,
      address: formData.address?.trim() || undefined,
      city: formData.city?.trim() || undefined,
      postalCode: formData.postalCode?.trim() || undefined,
      country: formData.country?.trim() || undefined,
      fiscalCode: formData.fiscalCode?.trim() || undefined,
      vatNumber: formData.vatNumber?.trim() || undefined,
      sdiCode: formData.sdiCode?.trim() || undefined,
      marketingConsent: formData.marketingConsent || false,
    };
    
    if (editingDonor) {
      updateMutation.mutate({ id: editingDonor.id, data: cleanFormData });
    } else {
      createMutation.mutate(cleanFormData);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questo donatore?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    // Validazione per assicurarsi che la pagina non sia negativa
    if (newPage >= 0) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Validazione per assicurarsi che rowsPerPage sia valido
    if (newRowsPerPage > 0) {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  // Validazione pagina corrente dopo cambio di rowsPerPage
  useEffect(() => {
    if (data?.pagination && data.pagination.total > 0) {
      const maxPage = Math.max(0, Math.ceil(data.pagination.total / rowsPerPage) - 1);
      if (page > maxPage) {
        setPage(maxPage);
      }
    }
  }, [data?.pagination, rowsPerPage, page]);

  // Definizione colonne per DataTable
  const columns: Column<Donor>[] = [
    {
      id: 'name',
      label: 'Nome e Cognome',
      minWidth: 200,
      format: (value, donor) => (
        <Typography variant="body2" fontWeight={500}>
          {donor.firstName} {donor.lastName}
        </Typography>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200,
      format: (value, donor) => (
        <Typography variant="body2" color="primary">
          {donor.email}
        </Typography>
      ),
    },
    {
      id: 'company',
      label: 'Azienda',
      minWidth: 150,
      format: (value, donor) => (
        <Typography variant="body2" color="text.secondary">
          {donor.company || '-'}
        </Typography>
      ),
    },
    {
      id: 'city',
      label: 'Città',
      minWidth: 120,
      format: (value, donor) => (
        <Typography variant="body2">
          {donor.city || '-'}
        </Typography>
      ),
    },
    {
      id: 'phone',
      label: 'Telefono',
      minWidth: 120,
      format: (value, donor) => (
        <Typography variant="body2">
          {donor.phone || '-'}
        </Typography>
      ),
    },
    {
      id: 'fiscalCode',
      label: 'Codice Fiscale',
      minWidth: 150,
      format: (value, donor) => (
        <Typography variant="body2" fontFamily="monospace">
          {donor.fiscalCode || '-'}
        </Typography>
      ),
    },
    {
      id: 'vatNumber',
      label: 'P.IVA',
      minWidth: 120,
      format: (value, donor) => (
        <Typography variant="body2" fontFamily="monospace">
          {donor.vatNumber || '-'}
        </Typography>
      ),
    },
    {
      id: 'marketingConsent',
      label: 'Consenso Marketing',
      minWidth: 150,
      align: 'center',
      format: (value, donor) => {
        const colors = donor.marketingConsent ? statusColors.marketingConsent : statusColors.marketingConsentDenied;
        return (
          <Chip
            label={donor.marketingConsent === true ? 'Sì' : donor.marketingConsent === false ? 'No' : 'N/A'}
            variant="default"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              backgroundColor: colors.backgroundColor,
              color: colors.color,
              borderColor: colors.borderColor,
            }}
          />
        );
      },
    },
    {
      id: 'createdAt',
      label: 'Data Creazione',
      minWidth: 120,
      format: (value, donor) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(donor.createdAt).toLocaleDateString('it-IT')}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Azioni',
      minWidth: 120,
      align: 'right',
      format: (value, donor) => (
        <ActionButtons
          onView={() => handleViewDonor(donor)}
          onEdit={() => handleOpenDialog(donor)}
          onDelete={() => handleDelete(donor.id)}
          showEdit={hasAnyRole(['ADMIN', 'SUPERVISOR'])}
          showDelete={hasAnyRole(['ADMIN', 'SUPERVISOR'])}
          size="small"
          spacing={0.5}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Caricamento donatori...</Typography>
      </Box>
    );
  }


  return (
    <PageLayout>
      <PageHeader>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Donatori</Typography>
          <Box display="flex" gap={2}>
            <FilterChip
              onClick={() => setFilterModalOpen(true)}
              hasActiveFilters={hasActiveFilters}
              activeFiltersCount={activeFiltersCount}
            />
            {hasAnyRole(['ADMIN', 'SUPERVISOR']) && (
              <Button
                variant="primary"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Nuovo Donatore
              </Button>
            )}
          </Box>
        </Box>
      </PageHeader>

      <ScrollableContent maxHeight="calc(100vh - 400px)">
        <DataTable
          columns={columns}
          data={data?.data || []}
          loading={isLoading}
          error={error?.message}
          emptyMessage="Nessun donatore trovato"
        />
      </ScrollableContent>
      
      <StandardPagination
        count={data?.pagination.total || 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDonor ? 'Modifica Donatore' : 'Nuovo Donatore'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Cognome"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Azienda"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Nome azienda (opzionale)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Telefono"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Indirizzo"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Città"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="CAP"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Paese"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </Grid>
              
              {/* Sezione Dati Fiscali */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                  Dati Fiscali
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Scegli se inserire il Codice Fiscale o la Partita IVA
                </Typography>
                
                {/* Pulsanti per scegliere il tipo di campo fiscale */}
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                  <Button
                    variant={fiscalFieldType === 'fiscalCode' ? 'primary' : 'outlined'}
                    startIcon={<Person />}
                    onClick={() => handleFiscalFieldTypeChange('fiscalCode')}
                    size="small"
                  >
                    Codice Fiscale
                  </Button>
                  <Button
                    variant={fiscalFieldType === 'vatNumber' ? 'primary' : 'outlined'}
                    startIcon={<Business />}
                    onClick={() => handleFiscalFieldTypeChange('vatNumber')}
                    size="small"
                  >
                    Partita IVA
                  </Button>
                </Box>
              </Grid>
              
              {/* Campo Codice Fiscale - mostrato solo se selezionato */}
              {fiscalFieldType === 'fiscalCode' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Codice Fiscale"
                    value={formData.fiscalCode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setFormData({ ...formData, fiscalCode: value });
                      if (value.length === 16) {
                        validateFiscalCode(value);
                      } else {
                        setFiscalCodeValidation(null);
                      }
                    }}
                    placeholder="RSSMRA80A01H501U"
                    inputProps={{ maxLength: 16 }}
                    helperText={
                      fiscalCodeValidation 
                        ? fiscalCodeValidation.isValid 
                          ? "✓ Codice Fiscale valido" 
                          : fiscalCodeValidation.error
                        : "16 caratteri alfanumerici"
                    }
                    error={fiscalCodeValidation ? !fiscalCodeValidation.isValid : false}
                    required
                  />
                </Grid>
              )}

              {/* Campo Partita IVA - mostrato solo se selezionato */}
              {fiscalFieldType === 'vatNumber' && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Partita IVA"
                    value={formData.vatNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, vatNumber: value });
                      if (value.length === 11) {
                        validateVatNumber(value);
                      } else {
                        setVatNumberValidation(null);
                      }
                    }}
                    placeholder="12345678901"
                    inputProps={{ maxLength: 11 }}
                    helperText={
                      vatNumberValidation 
                        ? vatNumberValidation.isValid 
                          ? "✓ Partita IVA valida" 
                          : vatNumberValidation.error
                        : "11 cifre"
                    }
                    error={vatNumberValidation ? !vatNumberValidation.isValid : false}
                    required
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  label="Codice Univoco SDI"
                  value={formData.sdiCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setFormData({ ...formData, sdiCode: value });
                    if (value.length >= 6) {
                      validateSdiCode(value);
                    } else {
                      setSdiCodeValidation(null);
                    }
                  }}
                  placeholder="ABC12345"
                  inputProps={{ maxLength: 7 }}
                  helperText={
                    sdiCodeValidation 
                      ? sdiCodeValidation.isValid 
                        ? "✓ Codice SDI valido" 
                        : sdiCodeValidation.error
                      : "6-7 caratteri alfanumerici"
                  }
                  error={sdiCodeValidation ? !sdiCodeValidation.isValid : false}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="PEC"
                  value={formData.pec}
                  onChange={(e) => {
                    setFormData({ ...formData, pec: e.target.value });
                  }}
                  placeholder="pec@azienda.it"
                  type="email"
                  helperText="Indirizzo PEC (opzionale)"
                />
              </Grid>
              
              {/* Sezione GDPR */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                  Consensi GDPR
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.gdprConsent}
                      onChange={(e) => setFormData({ ...formData, gdprConsent: e.target.checked })}
                      color="primary"
                      required
                    />
                  }
                  label={
                    <Typography variant="body2" color={!formData.gdprConsent ? 'error' : 'text.primary'}>
                      Consenso al trattamento dei dati personali (GDPR) *
                    </Typography>
                  }
                />
                <Typography variant="caption" color={!formData.gdprConsent ? 'error' : 'text.secondary'} display="block">
                  Obbligatorio per la gestione delle donazioni
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.marketingConsent}
                      onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Consenso per comunicazioni marketing"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Opzionale per newsletter e comunicazioni promozionali
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button type="submit" variant="primary">
              {editingDonor ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* GenericFilterModal */}
      <GenericFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filtri Donatori"
        fields={filterFields}
        values={filterValues}
        onUpdateValue={updateFilter}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      {/* Modale Visualizzazione Dettagli */}
      <ViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        title="Dettagli Donatore"
        fields={viewingDonor ? getDonorViewFields(viewingDonor) : []}
        actions={
          <Box>
            <Button
              variant="outlined"
              onClick={handleCloseViewModal}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                mr: 1,
              }}
            >
              Chiudi
            </Button>
            {hasAnyRole(['ADMIN', 'SUPERVISOR']) && (
              <Button
                variant="primary"
                onClick={() => {
                  handleCloseViewModal();
                  handleOpenDialog(viewingDonor);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Modifica
              </Button>
            )}
          </Box>
        }
      />
      
      {/* Snackbar per notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default Donors;
